import { Box, Button, Drawer, Flex, Text } from '@mantine/core';
import React from 'react';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { BankAccountForm } from '@dealstack/frontend/shared/components/BankAccountForm/BankAccountForm.tsx';
import { VerificationCode } from '@dealstack/frontend/shared/components/VerificationCode/VerificationCode';
import { IBAN_COMPONENT_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import type {
	BankAccountDetails,
	BankAccountFormValues,
} from '@dealstack/frontend/shared/components/BankAccountForm/types.ts';
import type { BankAccountVerifyFn } from '@dealstack/frontend/shared/components/BankAccountForm/useBankAccountForm.types.ts';

interface Props {
	opened: boolean;
	onClose: () => void;
	title: string;
	headingTestId?: string;
	hasPermission: boolean;
	bankAccount: BankAccountDetails | null;
	isRequestLoading: boolean;
	onSubmit: (formValues: BankAccountFormValues, bankAccountId?: string) => void;
	inferredCountryCode?: string;
	verifyIdentifiers?: BankAccountVerifyFn;
	verification?: {
		title: string;
		label: string;
		submitLabel: string;
		code: string;
		onCodeChange: (code: string) => void;
		errorMessage?: string;
		onVerify: () => void;
		isVerifying: boolean;
	} | null;
}

export const BankAccountFormVerificationDrawer = ({
	opened,
	onClose,
	title,
	headingTestId,
	hasPermission,
	bankAccount,
	isRequestLoading,
	onSubmit,
	inferredCountryCode,
	verifyIdentifiers,
	verification,
}: Props) => (
	<Drawer
		opened={opened}
		onClose={onClose}
		position="right"
		transitionProps={drawerTransitionProps}
		title={<span data-testid={headingTestId}>{verification ? verification.title : title}</span>}
	>
		{verification ? (
			<Flex direction="column" justify="space-between" gap="1rem" h="100%">
				<VerificationCode
					numberOfVerificationCodeInputs={6}
					value={verification.code}
					onChange={verification.onCodeChange}
					label={
						<Box mb="1rem">
							<Text>{verification.label}</Text>
						</Box>
					}
					errorMessage={verification.errorMessage}
					size="MEDIUM"
				/>
				<Button
					variant="primary"
					w="100%"
					disabled={verification.code.length !== 6 || verification.isVerifying}
					onClick={verification.onVerify}
					data-testid={IBAN_COMPONENT_ELEMENT_ID.VERIFY_CODE_BUTTON}
				>
					{verification.submitLabel}
				</Button>
			</Flex>
		) : (
			<BankAccountForm
				hasEditPermission={hasPermission}
				onSubmit={onSubmit}
				onCancel={onClose}
				isRequestLoading={isRequestLoading}
				bankAccount={bankAccount}
				inferredCountryCode={inferredCountryCode}
				verifyIdentifiers={verifyIdentifiers}
			/>
		)}
	</Drawer>
);
